import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Typography,
  Link,
  Button,
  Snackbar,
  Alert,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { DataGrid } from '@mui/x-data-grid';
import MultiSelectTagFilter from '@components/filter-component/MultiSelectTagFilter';
import { TAG_THEME } from '@constants/analysisLenses';
import { useFetchAssignedTags, useFetchTranscriptListPagination } from '@hooks/getQueries';
import { formatDate } from '@utils/dateFormat';
import { formatInTitleCase } from '@utils/constants';

const chipSx = {
  fontSize: 10,
  bgcolor: (theme) => {
    const tagName = theme.ownerState?.label;
    return TAG_THEME[tagName]?.background || '#e3f2fd';
  },
  color: (theme) => {
    const tagName = theme.ownerState?.label;
    return TAG_THEME[tagName]?.text || '#7d5ae8';
  },
};

const MAX_SELECTION = 20;

export default function TranscriptSelectionDialog({ open, onClose, onStart, id }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredTagList, setFilteredTagList] = useState([]);
  const [selected, setSelected] = useState([]);
  const [persistentSelection, setPersistentSelection] = useState(new Set());
  const [selectionLimitReached, setSelectionLimitReached] = useState(false);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
  const [rowCountState, setRowCountState] = useState(0);
  const [displayedRows, setDisplayedRows] = useState([]);

  const { data: assignedTagList, isFetching } = useFetchAssignedTags(id, searchQuery);

  const queryParams = useMemo(
    () => ({
      limit: paginationModel.pageSize,
      offset: paginationModel.page * paginationModel.pageSize,
    }),
    [paginationModel.page, paginationModel.pageSize],
  );

  const {
    data: transcriptsResponse,
    isFetching: txLoading,
    refetch,
  } = useFetchTranscriptListPagination(id, filteredTagList, queryParams);

  useEffect(() => {
    if (open) refetch();
  }, [open, refetch]);
  useEffect(() => {
    refetch();
  }, [queryParams, filteredTagList, refetch]);

  // Keep rowCount stable across refetches to prevent page reset to 0
  useEffect(() => {
    const total = transcriptsResponse?.pagination?.total;
    setRowCountState((prev) => (typeof total === 'number' ? total : prev));
  }, [transcriptsResponse]);

  // update displayedRows only when new data arrives
  useEffect(() => {
    if (transcriptsResponse?.transcripts) setDisplayedRows(transcriptsResponse.transcripts);
  }, [transcriptsResponse]);

  const handleStart = () => {
    const selectedIds = Array.from(persistentSelection);
    onStart(selectedIds);
  };

  return (
    <Dialog open={open} fullWidth maxWidth="lg" sx={{ p: 0 }}>
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          fontWeight: 600,
          height: '56px',
        }}
      >
        <Box display={'flex'} alignItems={'center'} gap={0.4}>
          Select Transcripts
        </Box>
        <Box>
          <CloseIcon sx={{ ml: 'auto', cursor: 'pointer' }} onClick={onClose} />
        </Box>
      </DialogTitle>
      <Divider />
      <DialogContent>
        <Typography>
          Select one or more transcripts below and press Start Chat to run a deep search on your
          selections. If you don’t choose any and skip selection, the deep search will include all
          transcripts.
        </Typography>

        <Box display={'flex'} justifyContent={'space-between'} alignItems={'center'} mt={2} mb={1}>
          <Typography variant="subtitle1">Transcripts</Typography>
          <Box display={'flex'} alignItems={'center'} gap={1}>
            <Box width={'200px'} mr={1}>
              <MultiSelectTagFilter
                options={assignedTagList}
                searchQuery={searchQuery}
                isFetching={isFetching}
                setSearchQuery={setSearchQuery}
                label="Filter By Tag"
                searchControl
                onFilterChange={setFilteredTagList}
                isFiltering={txLoading}
              />
            </Box>
          </Box>
        </Box>
        <Divider />

        <DataGrid
          getRowHeight={() => 'auto'}
          columns={[
            {
              field: 'name',
              headerName: 'Transcript Name',
              flex: 1,
              minWidth: 200,
              renderCell: (params) => (
                <Link
                  href={`/interview/${id}/transcripts/${params.id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {params.value || `Transcript ${params.row.index ?? ''}`}
                </Link>
              ),
            },
            {
              field: 'email',
              headerName: 'Email',
              flex: 1,
              minWidth: 240,
              renderCell: (p) => (p.value ? <>{p.value}</> : <>Unknown</>),
            },
            {
              field: 'participant_type',
              headerName: 'Participant Type',
              flex: 1,
              minWidth: 120,
              renderCell: (p) => (
                <Chip
                  label={p.value}
                  size="small"
                  sx={{
                    ...chipSx,
                    bgcolor: TAG_THEME[p.value]?.background || '#e3f2fd',
                    color: TAG_THEME[p.value]?.text || '#7d5ae8',
                  }}
                />
              ),
            },
            {
              field: 'location',
              headerName: 'Location',
              flex: 1,
              minWidth: 100,
              valueFormatter: (p) => p.value || '--',
            },
            {
              field: 'created_at',
              headerName: 'Submission Date',
              flex: 1,
              minWidth: 100,
              renderCell: (p) => (p.value ? <>{formatDate(p.value)}</> : <>Unknown</>),
            },
            {
              field: 'assigned_tags',
              headerName: 'Applied Tags',
              flex: 1,
              minWidth: 190,
              renderCell: (params) => {
                const tags = params.value;
                if (!Array.isArray(tags) || tags.length === 0)
                  return <Typography color="text.secondary">No tags</Typography>;
                return (
                  <Box display="flex" flexDirection="column" gap={1} flexWrap={'wrap'}>
                    {tags.map((tagGroup) => (
                      <Box
                        key={tagGroup.tag_key_id}
                        sx={{
                          backgroundColor: '#e3f2fd',
                          borderRadius: 1,
                          width: 'fit-content',
                          px: 1,
                          my: 0.3,
                          display: 'flex',
                          alignItems: 'center',
                        }}
                      >
                        <Typography variant="caption" fontWeight="bold" py={0.4}>
                          {formatInTitleCase(tagGroup.tag_key)}:
                        </Typography>{' '}
                        <Typography variant="caption" component="span" py={0.4} sx={{ ml: 0.5 }}>
                          {tagGroup.values
                            .map((val) => formatInTitleCase(val.value_key))
                            .join(', ')}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                );
              },
            },
          ]}
          rows={
            displayedRows?.map((r, idx) => ({
              ...r,
              index: paginationModel.page * paginationModel.pageSize + idx + 1,
            })) ?? []
          }
          checkboxSelection
          rowCount={rowCountState}
          paginationModel={paginationModel}
          onPaginationModelChange={(model) =>
            setPaginationModel({ page: model.page, pageSize: model.pageSize })
          }
          paginationMode="server"
          rowSelectionModel={selected}
          onRowSelectionModelChange={(newSelection) => {
            if (newSelection.length > MAX_SELECTION) {
              setSelectionLimitReached(true);
              const trimmed = Array.from(newSelection).slice(0, MAX_SELECTION);
              const nextSet = new Set(trimmed);
              setPersistentSelection(nextSet);
              setSelected(Array.from(nextSet));
              return;
            }
            const nextSet = new Set(newSelection);
            setPersistentSelection(nextSet);
            setSelected(Array.from(nextSet));
          }}
          keepNonExistentRowsSelected
          loading={txLoading}
        />
      </DialogContent>
      <DialogActions>
        <Button variant="text" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleStart} disabled={selected?.length === 0}>
          Start Chat
        </Button>
      </DialogActions>

      <Snackbar
        open={selectionLimitReached}
        autoHideDuration={2500}
        onClose={() => setSelectionLimitReached(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSelectionLimitReached(false)}
          severity="info"
          sx={{
            width: '100%',
            backgroundColor: 'black',
            color: 'white',
            '& .MuiAlert-icon': { color: 'white' },
          }}
        >
          You can select up to {MAX_SELECTION} transcripts.
        </Alert>
      </Snackbar>
    </Dialog>
  );
}
